import {FC, useEffect, useRef} from "react";
import {ParticipantAdminDto} from "../../types/contracts";
import {IParticipantAdditionalService} from "../../types";
import {format, parseISO} from "date-fns";
import {useEventsContext} from "../../providers/EventsProvider";

interface ParticipantModalProps {
    participant: ParticipantAdminDto | null;
    isOpen: boolean;
    onClose: () => void;
}

const ParticipantModal: FC<ParticipantModalProps> = ({participant, isOpen, onClose}) => {
    const modalRef = useRef<HTMLDivElement | null>(null);

    const { selectedEvent } = useEventsContext()


    useEffect(() => {
        const handleOutsideClick = (e: MouseEvent) => {
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
        };
        if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isOpen, onClose]);

    if (!isOpen || !participant) {
        return <></>;
    }


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div ref={modalRef} className="bg-white w-full max-w-xl mx-4 rounded-lg shadow-lg overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold">Podrobnosti objednávky</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        ✕
                    </button>
                </div>
                <div className="p-4 space-y-4">
                    <div className="flex flex-wrap">
                        <div className="w-full md:w-1/2 p-2 flex flex-col space-y-1">
                            <p className="text-md font-semibold">Jméno a příjmení:</p>
                            <p className="text-sm text-gray-500">{participant.name} {participant.lastname}</p>
                        </div>

                        <div className="w-full md:w-1/2 p-2 flex flex-col space-y-1">
                            <p className="text-md font-semibold">Číslo objednávky:</p>
                            <p className="text-sm text-gray-500">{participant.orderNumber}</p>
                        </div>
                        <div className="w-full md:w-1/2 p-2 flex flex-col space-y-1">
                            <p className="text-md font-semibold">Detail ubytování:</p>
                            {participant.accommodation.isSelfArrangedAccommodation ? (
                                <>
                                    <p className="text-sm text-gray-500">
                                        {participant.accommodation.customAccommodationName}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Adresa: {participant.accommodation.customAccommodationAddress}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-500">{participant.accommodation.name}</p>
                                    {participant.accommodation.isPricePerNight && (
                                        <p className="text-sm text-gray-500">
                                            Cena za
                                            noc: {participant.accommodation.price} {selectedEvent?.currency}
                                        </p>
                                    )}
                                    <p className="text-sm text-gray-500">
                                        Celková
                                        cena: {participant.accommodation.totalPrice} {selectedEvent?.currency}
                                    </p>
                                </>
                            )}
                        </div>
                        <div className="w-full md:w-1/2 p-2 flex flex-col space-y-1">
                            <p className="text-md font-semibold">Celková cena objednávky:</p>
                            <p className="text-sm text-gray-500">{participant.orderAmount} {selectedEvent?.currency}</p>
                        </div>
                    </div>

                    <hr className="border-t border-gray-300" />

                    {/* Additional Services */}
                    <div>
                        <h3 className="text-md font-semibold mb-2">Neuplatněné služby</h3>
                        {participant.appliedAdditionalServices &&
                        participant.appliedAdditionalServices.filter(
                            (service: IParticipantAdditionalService) =>
                                !service.applied
                        ).length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200 divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-4 py-2 text-left font-medium text-gray-700 border-b border-gray-200"
                                        >
                                            Název
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                    {participant.additionalServices
                                        .filter(
                                            (service: IParticipantAdditionalService) =>
                                                service.dateApplied !== null
                                        )
                                        .map((service: IParticipantAdditionalService) => (
                                            <tr key={service.id}>
                                                <td className="px-4 py-2 text-gray-700">
                                                    {service.name}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-500">Žádné neaplikované nakoupené</p>
                        )}
                    </div>

                    {/* Applied Additional Services */}
                    <div>
                        <h3 className="text-md font-semibold mb-2">Uplatněné služby</h3>
                        {participant.appliedAdditionalServices &&
                        participant.appliedAdditionalServices.filter(
                            (service: IParticipantAdditionalService) => service.applied
                        ).length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200 divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th
                                            scope="col"
                                            className="px-4 py-2 text-left font-medium text-gray-700 border-b border-gray-200"
                                        >
                                            Název
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-2 text-left font-medium text-gray-700 border-b border-gray-200"
                                        >
                                            Datum využití
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                    {participant.appliedAdditionalServices
                                        .filter(
                                            (service: IParticipantAdditionalService) =>
                                                service.applied
                                        )
                                        .map((service: IParticipantAdditionalService) => (
                                            <tr key={service.id}>
                                                <td className="px-4 py-2 text-gray-700">
                                                    {service.name}
                                                </td>
                                                <td className="px-4 py-2 text-gray-700">
                                                    {format(
                                                        parseISO(service.dateApplied?.toLocaleString()),
                                                        "dd.MM.yyyy HH:mm"
                                                    ) ?? "Bez data"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <p className="text-gray-500">Zatím žádné využité nákupy</p>
                        )}
                    </div>
                </div>
                <div className="p-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 focus:outline-none"
                    >
                        Zavřít
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ParticipantModal;
